import {useCallback, useRef} from 'react';
import type {MouseEvent} from 'react';

export interface OverlayCloseProps {
    onMouseDown: (e: MouseEvent) => void;
    onClick: (e: MouseEvent) => void;
}

/**
 * Returns props to spread on a modal overlay element so it dismisses only on a
 * genuine outside click — i.e. both the mousedown AND the resulting click happen
 * directly on the overlay itself, not on the modal content.
 *
 * Without this, a text-selection drag that starts inside the modal content and
 * ends (mouseup) on the overlay produces a `click` event dispatched on the
 * overlay (the common ancestor), which would falsely close the modal (CTHS-249).
 *
 * @param onClose called when a genuine outside click is detected
 * @param enabled when false the overlay never closes (e.g. while saving)
 */
export function useOverlayClose(onClose: () => void, enabled = true): OverlayCloseProps {
    const pressStartedOnOverlay = useRef(false);

    const onMouseDown = useCallback((e: MouseEvent) => {
        pressStartedOnOverlay.current = e.target === e.currentTarget;
    }, []);

    const onClick = useCallback((e: MouseEvent) => {
        const genuineOutsideClick = pressStartedOnOverlay.current && e.target === e.currentTarget;
        pressStartedOnOverlay.current = false;
        if (enabled && genuineOutsideClick) {
            onClose();
        }
    }, [enabled, onClose]);

    return {onMouseDown, onClick};
}

export default useOverlayClose;
